/**
 * Estado do compartilhamento de tela DESTE lado, compartilhado entre o botão da barra, a faixa "Você está
 * compartilhando" e o aviso de erro. Mora acima da troca de janela (guia / flutuante), senão o erro e o "pendente"
 * se perderiam ao mover a sala. "Compartilhando" vem da própria sala (`isScreenShareEnabled`), então também
 * acompanha o botão "Interromper compartilhamento" do navegador. Sem provider (testes de peças soltas) o recurso
 * simplesmente não existe: `supported: false`.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useLocalParticipant } from '@livekit/components-react';
import { screenShareErrorMessage, startScreenShare, stopScreenShare, supportsScreenShare } from './screenShare';

const UNAVAILABLE = Object.freeze({ supported: false, sharing: false, pending: false, error: null, toggle() {}, stop() {}, clearError() {} });

const ScreenShareContext = createContext(null);

/** @returns {{ supported: boolean, sharing: boolean, pending: boolean, error: string|null, toggle: (win?: Window) => Promise<void>, stop: () => Promise<void>, clearError: () => void }} */
export function useScreenShare() {
    return useContext(ScreenShareContext) ?? UNAVAILABLE;
}

export function ScreenShareProvider({ children }) {
    const { localParticipant, isScreenShareEnabled } = useLocalParticipant();
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(null);
    const busy = useRef(false);
    const supported = useMemo(() => supportsScreenShare(), []);

    const run = useCallback(async (action) => {
        if (busy.current) return;
        busy.current = true;
        setPending(true);
        setError(null);
        try {
            await action();
        } catch (failure) {
            setError(screenShareErrorMessage(failure));
        } finally {
            busy.current = false;
            setPending(false);
        }
    }, []);

    // `win`: a janela do clique (a flutuante, quando a sala está nela): é dela o `getDisplayMedia`.
    const toggle = useCallback(
        (win) => run(() => (isScreenShareEnabled ? stopScreenShare(localParticipant) : startScreenShare(localParticipant, win))),
        [run, isScreenShareEnabled, localParticipant],
    );
    const stop = useCallback(() => run(() => stopScreenShare(localParticipant)), [run, localParticipant]);
    const clearError = useCallback(() => setError(null), []);

    const value = useMemo(
        () => ({ supported, sharing: Boolean(isScreenShareEnabled), pending, error, toggle, stop, clearError }),
        [supported, isScreenShareEnabled, pending, error, toggle, stop, clearError],
    );
    return <ScreenShareContext.Provider value={value}>{children}</ScreenShareContext.Provider>;
}
